import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2 } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useBlogCategories } from '@/hooks/data/useBlog'
import { useAlertContext } from '@/providers/AlertProvider'
import { imagesApi } from '@/api/images'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { userMessage } from '@/api/errors'
import type { Blog, BlogInput } from '@/types/domain'
import RichTextEditor from './RichTextEditor'

function slugify(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

function estimateReadTime(html: string): number {
  const words = html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 200))
}

interface BlogEditorFormProps {
  initial?: Blog
  onSubmit: (input: BlogInput) => Promise<void>
  submitLabel: string
}

export default function BlogEditorForm({ initial, onSubmit, submitLabel }: BlogEditorFormProps) {
  const categories = useBlogCategories()
  const navigate = useNavigate()
  const { success: showSuccess, error: showError } = useAlertContext()

  const [title, setTitle] = useState(initial?.title ?? '')
  const [slug, setSlug] = useState(initial?.slug ?? '')
  const [slugTouched, setSlugTouched] = useState(!!initial?.slug)
  const [category, setCategory] = useState(initial?.category ?? '')
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? '')
  const [content, setContent] = useState(initial?.content ?? '')
  const [readTimeMinutes, setReadTimeMinutes] = useState(initial?.readTimeMinutes ?? 1)
  const [readTimeTouched, setReadTimeTouched] = useState(!!initial)
  const [imageUrl, setImageUrl] = useState(initial?.imageUrl ?? '')
  const [publishedOn, setPublishedOn] = useState(initial?.publishedOn?.slice(0, 10) ?? new Date().toISOString().slice(0, 10))
  const [isPublished, setIsPublished] = useState(initial?.isPublished ?? true)
  const [submitting, setSubmitting] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const imageInputRef = useRef<HTMLInputElement>(null)

  // Auto-derive the slug from the title until the admin edits it directly — the backend
  // also derives it server-side if omitted, this is just an editable live preview.
  useEffect(() => {
    if (!slugTouched) setSlug(slugify(title))
  }, [title, slugTouched])

  // Auto-estimate read time from content (~200 wpm) until the admin overrides it directly.
  useEffect(() => {
    if (!readTimeTouched) setReadTimeMinutes(estimateReadTime(content))
  }, [content, readTimeTouched])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) {
      showError('Title required', 'Enter a title before saving.')
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        title: title.trim(),
        slug: slug.trim() || undefined,
        excerpt: excerpt.trim(),
        content,
        category: category.trim(),
        publishedOn: publishedOn ? new Date(`${publishedOn}T00:00:00.000Z`).toISOString() : undefined,
        readTimeMinutes,
        imageUrl: imageUrl.trim() || undefined,
        isPublished,
      })
      showSuccess('Saved', 'Post saved successfully.')
      navigate({ to: '/blog' })
    } catch (err) {
      showError('Save failed', (err as Error)?.message || 'Failed to save post.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass = 'w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm'
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-3xl">
      <div>
        <label className={labelClass}>
          Title <span className="text-red-500">*</span>
        </label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} required className={inputClass} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Slug</label>
          <input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value)
              setSlugTouched(true)
            }}
            placeholder="auto-generated from title"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Category</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} list="blog-categories" className={inputClass} />
          <datalist id="blog-categories">
            {categories.data?.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
      </div>

      <div>
        <label className={labelClass}>Excerpt</label>
        <textarea value={excerpt} onChange={(e) => setExcerpt(e.target.value)} rows={2} className={inputClass} />
      </div>

      <div>
        <label className={labelClass}>Content</label>
        <RichTextEditor value={content} onChange={setContent} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelClass}>Featured image</label>
          <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://… or upload below" className={inputClass} />
          {/* Native upload — TenantService ImagesController (POST /api/v1/images, category
              "blog"). The stored URL is gateway-relative, so the preview renders it through
              resolveMediaUrl (an admin-host-relative URL would 404, same bug fixed for
              campaigns/hero-slides). */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              e.target.value = ''   // allow re-selecting the same file
              if (!file) return
              setUploadingImage(true)
              try {
                const stored = await imagesApi.upload(file, 'blog')
                setImageUrl(stored.url)
                showSuccess('Image uploaded')
              } catch (err) {
                showError(userMessage(err))
              } finally {
                setUploadingImage(false)
              }
            }}
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploadingImage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              Upload image
            </button>
            {imageUrl && (
              <img
                src={resolveMediaUrl(imageUrl)}
                alt="Featured preview"
                className="h-12 w-20 rounded-md object-cover bg-gray-100 dark:bg-gray-700"
              />
            )}
          </div>
        </div>
        <div>
          <label className={labelClass}>Date</label>
          <input type="date" value={publishedOn} onChange={(e) => setPublishedOn(e.target.value)} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Read time (min)</label>
          <input
            type="number"
            min={1}
            value={readTimeMinutes}
            onChange={(e) => {
              setReadTimeMinutes(Number(e.target.value))
              setReadTimeTouched(true)
            }}
            className={inputClass}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input id="isPublished" type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
        <label htmlFor="isPublished" className="text-sm text-gray-700 dark:text-gray-300">
          Published
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm disabled:opacity-40">
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button type="button" onClick={() => navigate({ to: '/blog' })} className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm">
          Cancel
        </button>
      </div>
    </form>
  )
}
